import React from 'react';
import { TextField, InputAdornment, IconButton, styled, Box } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { Visibility, VisibilityOff, ErrorOutline } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Custom input variants
export type InputVariant = 'outlined' | 'filled' | 'standard';
export type InputSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomInputProps extends Omit<TextFieldProps, 'variant' | 'size'> {
  variant?: InputVariant;
  size?: InputSize;
  showPassword?: boolean;
  onPasswordToggle?: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  description?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
}

// Styled input component
const StyledTextField = styled(TextField)<CustomInputProps>(({ theme, size: customSize, error }) => {
  const sizeConfig = {
    small: {
      '& .MuiInputBase-root': {
        height: { xs: 40, sm: 36 },
        fontSize: { xs: '0.875rem', sm: '0.75rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '0.875rem', sm: '0.75rem' },
        transform: 'translate(12px, 10px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(12px, -6px) scale(0.75)',
        },
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        height: { xs: 48, sm: 44 },
        fontSize: { xs: '1rem', sm: '0.875rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '1rem', sm: '0.875rem' },
      },
    },
    large: {
      '& .MuiInputBase-root': {
        height: { xs: 56, sm: 52 },
        fontSize: { xs: '1.125rem', sm: '1rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '1.125rem', sm: '1rem' },
      },
    },
  };

  return {
    ...sizeConfig[customSize || 'medium'],
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1),
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: error ? theme.palette.error.main : theme.palette.grey[300],
        borderWidth: 1.5,
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 0 0 2px ${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2
        )}`,
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: theme.palette.grey[200],
        },
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1, 1, 0, 0),
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
      },
    },
    '& .MuiInput-root': {
      '&:before': {
        borderBottomColor: theme.palette.grey[300],
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: theme.palette.primary.main,
      },
      '&.Mui-focused:after': {
        borderBottomColor: error ? theme.palette.error.main : theme.palette.primary.main,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginTop: theme.spacing(0.5),
      fontSize: { xs: '0.875rem', sm: '0.75rem' },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },
  };
});

const CharacterCount = styled('span')(({ theme }) => ({
  fontSize: { xs: '0.875rem', sm: '0.75rem' },
  color: theme.palette.text.secondary,
  float: 'right',
  marginTop: theme.spacing(0.5),
}));

export const Input: React.FC<CustomInputProps> = ({
  variant = 'outlined',
  size = 'medium',
  type = 'text',
  showPassword,
  onPasswordToggle,
  startIcon,
  endIcon,
  description,
  maxLength,
  showCharacterCount,
  value = '',
  error,
  helperText,
  label,
  ...props
}) => {
  const isPassword = type === 'password';
  const currentType = isPassword && showPassword ? 'text' : type;
  const characterCount = typeof value === 'string' ? value.length : 0;
  const isOverLimit = maxLength ? characterCount > maxLength : false;

  const renderStartAdornment = () => {
    if (!startIcon) return undefined;
    return <InputAdornment position="start">{startIcon}</InputAdornment>;
  };

  const renderEndAdornment = () => {
    const elements: React.ReactNode[] = [];

    // Add custom end icon
    if (endIcon && !isPassword) {
      elements.push(endIcon);
    }

    // Add password toggle
    if (isPassword && onPasswordToggle) {
      elements.push(
        <IconButton
          key="password-toggle"
          aria-label="toggle password visibility"
          onClick={onPasswordToggle}
          edge="end"
          size="small"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      );
    }

    // Add error icon
    if (error && !isPassword) {
      elements.push(<ErrorOutline key="error-icon" color="error" fontSize="small" />);
    }

    return elements.length > 0 ? (
      <InputAdornment position="end">
        {elements.map((element, index) => (
          <React.Fragment key={index}>{element}</React.Fragment>
        ))}
      </InputAdornment>
    ) : undefined;
  };

  const displayError = error || isOverLimit;
  const displayHelperText = displayError
    ? isOverLimit
      ? `Character limit exceeded (${characterCount}/${maxLength})`
      : helperText
    : description || helperText;

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTextField
        {...props}
        variant={variant}
        size={size as 'small' | 'medium' | undefined}
        type={currentType}
        value={value}
        error={displayError}
        label={label}
        helperText={displayHelperText}
        inputProps={{
          maxLength,
          ...props.inputProps,
        }}
        InputProps={{
          startAdornment: renderStartAdornment(),
          endAdornment: renderEndAdornment(),
          ...props.InputProps,
        }}
      />
      {showCharacterCount && maxLength && (
        <CharacterCount>
          {characterCount}/{maxLength}
        </CharacterCount>
      )}
    </Box>
  );
};

export default Input;
